export type RenderQuality = 'high' | 'medium' | 'low';

/** Next lighter profile – used when a device loses its WebGL context. */
export const LOWER_QUALITY: Readonly<Partial<Record<RenderQuality, RenderQuality>>> = { high: 'medium', medium: 'low' };

/**
 * Per-device quality profile, chosen at start: ?quality=… wins, then a stored
 * fallback (after a lost WebGL context), then the device class (touch → medium).
 */
export interface QualityProfile {
  /** Shadow map resolution, 0 = no sun shadows. */
  readonly shadowMapSize: number;
  /** MSAA samples of the post-process pipeline; 1 without bloom = no post pipeline (canvas anti-aliasing). */
  readonly msaaSamples: number;
  readonly bloom: boolean;
  /** Equirectangular sky image (tonemapped JPG). */
  readonly skyTexture: string;
  /** Caps the device pixel ratio – protects fill-rate on high-DPI phones. */
  readonly maxDevicePixelRatio: number;
  /**
   * Multiplies the tone-mapping exposure. Profiles without bloom lose its glow on bright surfaces and looked darker
   * on phones (user 2026-10-03) – a little more exposure makes up for it.
   */
  readonly exposureScale: number;
  /** Strength of the runner's own key light (RenderingConfig.characterLight) – phones the most, 0 = off. */
  readonly characterLight: number;
}

/** An emissive material of a loaded asset that flashes like a site warning lamp (rendering/MaterialFlasher). */
export interface FlashingMaterialConfig {
  readonly assetId: string;
  /** Material name as exported from Blender (e.g. MAT_WARNLIGHT_LENS). */
  readonly material: string;
  readonly hz: number;
  /** Share of a period the lamp is lit (0..1). */
  readonly duty: number;
  /** Glow left between the flashes (share of the authored intensity). */
  readonly floor: number;
}

/** Linear fog band of a material as multiples of the scene fog start/end (rendering/fogStretch.ts). */
export interface FogScale {
  readonly start: number;
  readonly end: number;
}

/**
 * Landmarks (ad gantry over the road) stay visible further than the track around them: the
 * materials of assets carrying `tag` (asset manifest metadata) get a stretched fog band. Start > 1
 * keeps them legible further; the end stays just beyond the distance at which their module spawns
 * (LevelConfig.segmentsAhead), so they emerge from the haze instead of popping in.
 */
export interface LandmarkFogConfig {
  readonly tag: string;
  readonly scale: FogScale;
}

export interface RenderingConfig {
  readonly antialias: boolean;
  /** Fallback cap; the quality profile's value wins. */
  readonly maxDevicePixelRatio: number;
  readonly clearColor: string;
  readonly fog: {
    readonly enabled: boolean;
    /** Matches the sky color just above the horizon, so the track fades into the sky. */
    readonly color: string;
    readonly start: number;
    readonly end: number;
  };
  /** Hemispheric fill light – only without an environment (IBL) texture. */
  readonly ambientLightIntensity: number;
  readonly sunIntensity: number;
  readonly sunColor: string;
  /** Sun direction (light travel direction, normalized at runtime). */
  readonly sunDirection: readonly [number, number, number];
  /** Image based lighting + sky (CC0 HDRI, see design/00_master/textures). */
  readonly environment: {
    readonly enabled: boolean;
    /**
     * Prefiltered environment (.env, baked once on a desktop GPU with tools/env-baker
     * from the CC0 HDRI) – no HDR filtering at startup, which overloads mobile GPUs.
     */
    readonly envUrl: string;
    readonly intensity: number;
    /**
     * Rotates the environment (image based light) so its sun matches `sunDirection` (degrees). The visible
     * sky ignores it: PhotoDome samples the equirect by view direction (azimuth = (u − 0.25)·360°), so the
     * sky images are rolled to put the sun at the light's azimuth instead.
     */
    readonly rotationDegrees: number;
    /** Sky dome radius (m) – beyond the far fog, inside the camera far plane (camera.config farPlane). */
    readonly skyRadius: number;
    /** City skyline panorama on an open band around the camera (texture: tools/blender/assets/BLD_Skyline_A.py). */
    readonly skyline: {
      readonly url: string;
      /** Band radius (m) – inside the sky dome; not fogged, the texture carries its own haze. */
      readonly radius: number;
      /** Elevation the texture spans (degrees) – must match SKYLINE_ELEVATION of the generator. */
      readonly elevationDegrees: readonly [number, number];
      /** Haze colour painted into the texture (FOG in BLD_Skyline_A.py) – the band is tinted from it to the fog. */
      readonly paintedHaze: string;
    } | null;
  };
  /** Plain ground under the whole world that follows the camera – no void at the start or beside junctions. */
  readonly horizonGround: {
    readonly color: string;
    /** Edge length (m); should exceed twice the fog end. */
    readonly size: number;
    /** Slightly below the module side ground (-0.02 m) to avoid z-fighting. */
    readonly height: number;
  };
  readonly toneMapping: {
    readonly enabled: boolean;
    readonly exposure: number;
    readonly contrast: number;
  };
  readonly shadows: {
    /** Half size of the orthographic shadow frustum around the player (m). */
    readonly extent: number;
    /** Shadow frustum depth along the light direction (m). */
    readonly depth: number;
    /** How far ahead of the player the frustum center is placed (m). */
    readonly lookAhead: number;
    readonly bias: number;
    readonly normalBias: number;
    /** 0 = black shadows, 1 = no shadows. */
    readonly darkness: number;
  };
  readonly quality: Readonly<Record<RenderQuality, QualityProfile>>;
  /** Fixed simulation step (seconds). Rendering interpolates between steps. */
  readonly fixedTimeStep: number;
  /** Frames longer than this are clamped to avoid a spiral of death (seconds). */
  readonly maxFrameTime: number;
  /** Purely visual tuning of the player placeholder (no gameplay effect). */
  readonly playerView: {
    readonly leanDegreesPerLateralSpeed: number;
    readonly maxLeanDegrees: number;
    readonly slideTiltDegrees: number;
    readonly hitTiltDegrees: number;
    readonly deadTiltDegrees: number;
    readonly poseDamping: number;
    /**
     * How fast the character turns into a track heading it is not already following (1/s) –
     * jumps that no turn bend covers (e.g. pushed back after an auto-turn).
     */
    readonly turnYawDamping: number;
    /**
     * At a 90° junction turn the body runs a smooth bend (player/turnBend.ts) from this many
     * meters before the corner to this many meters after it, facing the way it moves. Max corner
     * cut ≈ 0.35 × this (the inner lane stays 1.1 m from the road edge). A turn decided less
     * than this before its corner starts the bend at the decision and swings out past the corner.
     */
    readonly turnBendLength: number;
    readonly runBobAmplitude: number;
    readonly runBobStepsPerMeter: number;
    readonly invulnerableBlinkHz: number;
  };
  /** Decoration lamps that flash (SEG_21–23 sheets: yellow site warning lights). */
  readonly flashingMaterials: readonly FlashingMaterialConfig[];
  readonly landmarkFog: LandmarkFogConfig;
  /**
   * The runner's own key light (rendering/CharacterLight): the low sun ahead leaves the side the camera sees in shadow –
   * this light from behind the camera lights only the runner's meshes (user 2026-10-03: more contrast on phones).
   */
  readonly characterLight: {
    /** Before the quality profile's scale. */
    readonly intensity: number;
    readonly color: string;
    /** Specular share of the light colour (low: no glossy hot spots on the clothes). */
    readonly specular: number;
    /** Angle above the horizon the light comes down at (degrees). */
    readonly elevationDegrees: number;
    /** Turned from the track heading (degrees, + = light travels to the right – it comes from the left behind). */
    readonly sideDegrees: number;
    /** Share kept where the sun is dimmed (night, tunnels) – the runner lamp lights it there. */
    readonly minDaylight: number;
  };
}

export const RENDERING_CONFIG: RenderingConfig = {
  antialias: true,
  maxDevicePixelRatio: 2,
  // Evening sun like the segment sheets ("goldene Stunde"): warm haze matching the sky's horizon ahead.
  clearColor: '#cdb99c',
  fog: {
    enabled: true,
    color: '#cdb99c',
    start: 70,
    end: 170,
  },
  ambientLightIntensity: 0.75,
  sunIntensity: 3.1,
  sunColor: '#ffb878',
  // Low sun ahead-right of the runner: azimuth 40°, elevation 14° (long warm shadows, backlight as on the
  // sheets). The sky image sun sits at the same azimuth (sky_sunset_*.jpg are rolled accordingly).
  sunDirection: [-0.624, -0.242, -0.743],
  environment: {
    enabled: true,
    envUrl: '/assets/textures/environment/sky_sunset.env',
    intensity: 1,
    rotationDegrees: -23,
    skyRadius: 380,
    skyline: {
      url: '/assets/textures/environment/skyline_day.png',
      radius: 300,
      elevationDegrees: [-1, 11],
      paintedHaze: '#9ba0ae',
    },
  },
  horizonGround: {
    color: '#7b6f60',
    size: 760,
    height: -0.06,
  },
  toneMapping: {
    enabled: true,
    exposure: 0.95,
    contrast: 1.22,
  },
  shadows: {
    extent: 26,
    depth: 90,
    lookAhead: 14,
    bias: 0.0006,
    normalBias: 0.025,
    darkness: 0.25,
  },
  quality: {
    high: { shadowMapSize: 2048, msaaSamples: 4, bloom: true, skyTexture: '/assets/textures/environment/sky_sunset_4k.jpg', maxDevicePixelRatio: 2, exposureScale: 1, characterLight: 0.6 },
    medium: { shadowMapSize: 1024, msaaSamples: 1, bloom: false, skyTexture: '/assets/textures/environment/sky_sunset_2k.jpg', maxDevicePixelRatio: 1.5, exposureScale: 1.1, characterLight: 1 },
    low: { shadowMapSize: 0, msaaSamples: 1, bloom: false, skyTexture: '/assets/textures/environment/sky_sunset_2k.jpg', maxDevicePixelRatio: 1, exposureScale: 1.12, characterLight: 1 },
  },
  fixedTimeStep: 1 / 120,
  maxFrameTime: 0.1,
  playerView: {
    leanDegreesPerLateralSpeed: 1.6,
    maxLeanDegrees: 14,
    slideTiltDegrees: 62,
    hitTiltDegrees: 25,
    deadTiltDegrees: 85,
    poseDamping: 18,
    turnYawDamping: 14,
    turnBendLength: 1.8,
    runBobAmplitude: 0.05,
    runBobStepsPerMeter: 0.75,
    invulnerableBlinkHz: 8,
  },
  // Yellow site warning light (EN 12352 class lamp): about one short flash per second.
  flashingMaterials: [{ assetId: 'ENV_WARNING_LIGHT_A', material: 'MAT_WARNLIGHT_LENS', hz: 1.1, duty: 0.35, floor: 0.08 }],
  // Ad gantry (ENV_AD_GANTRY_A + its banner, advertising.config): day fog 70–170 m → 154–196 m, night
  // 45–150 m → 99–173 m. PBR blends the fog in linear space and the bright haze washes a dark print out
  // fast, so the banner is clear up to 154 m by day (night ≈ 100 m) and emerges over the last 40 m before
  // the point where its module spawns (~185 m from the camera: ≈ 5 % visible – no pop-in).
  landmarkFog: { tag: 'landmark', scale: { start: 2.2, end: 1.15 } },
  // From the left behind the camera (the sun stands ahead-right), warm white like the evening light. Compared on a
  // portrait phone (2026-10-03): 1.6 hardly showed on the dark navy workwear, 4 lifts it off the asphalt, 7 looks lit up.
  characterLight: { intensity: 4, color: '#fff1e0', specular: 0.25, elevationDegrees: 35, sideDegrees: 20, minDaylight: 0.35 },
};
